/**
 * Country Codes Utility
 * Production-level utility for country codes, phone number validation, and formatting
 * Used across registration, user profile, and OTP delivery systems
 */

/**
 * Comprehensive list of countries with their phone codes and mobile number lengths
 * Data structure: { name, code, dialCode, flag, minLength, maxLength }
 */
export const COUNTRY_CODES = [
  { name: "United States", code: "US", dialCode: "+1", flag: "🇺🇸", minLength: 10, maxLength: 10 },
  { name: "United Kingdom", code: "GB", dialCode: "+44", flag: "🇬🇧", minLength: 10, maxLength: 10 },
  { name: "Canada", code: "CA", dialCode: "+1", flag: "🇨🇦", minLength: 10, maxLength: 10 },
  { name: "Australia", code: "AU", dialCode: "+61", flag: "🇦🇺", minLength: 9, maxLength: 9 },
  { name: "Germany", code: "DE", dialCode: "+49", flag: "🇩🇪", minLength: 10, maxLength: 11 },
  { name: "France", code: "FR", dialCode: "+33", flag: "🇫🇷", minLength: 9, maxLength: 9 },
  { name: "India", code: "IN", dialCode: "+91", flag: "🇮🇳", minLength: 10, maxLength: 10 },
  { name: "China", code: "CN", dialCode: "+86", flag: "🇨🇳", minLength: 11, maxLength: 11 },
  { name: "Japan", code: "JP", dialCode: "+81", flag: "🇯🇵", minLength: 10, maxLength: 10 },
  { name: "Brazil", code: "BR", dialCode: "+55", flag: "🇧🇷", minLength: 10, maxLength: 11 },
  { name: "Mexico", code: "MX", dialCode: "+52", flag: "🇲🇽", minLength: 10, maxLength: 10 },
  { name: "Italy", code: "IT", dialCode: "+39", flag: "🇮🇹", minLength: 9, maxLength: 10 },
  { name: "Spain", code: "ES", dialCode: "+34", flag: "🇪🇸", minLength: 9, maxLength: 9 },
  { name: "South Korea", code: "KR", dialCode: "+82", flag: "🇰🇷", minLength: 9, maxLength: 10 },
  { name: "Netherlands", code: "NL", dialCode: "+31", flag: "🇳🇱", minLength: 9, maxLength: 9 },
  { name: "Switzerland", code: "CH", dialCode: "+41", flag: "🇨🇭", minLength: 9, maxLength: 9 },
  { name: "Sweden", code: "SE", dialCode: "+46", flag: "🇸🇪", minLength: 9, maxLength: 10 },
  { name: "Poland", code: "PL", dialCode: "+48", flag: "🇵🇱", minLength: 9, maxLength: 9 },
  { name: "Belgium", code: "BE", dialCode: "+32", flag: "🇧🇪", minLength: 9, maxLength: 9 },
  { name: "Austria", code: "AT", dialCode: "+43", flag: "🇦🇹", minLength: 10, maxLength: 13 },
  { name: "Norway", code: "NO", dialCode: "+47", flag: "🇳🇴", minLength: 8, maxLength: 8 },
  { name: "Denmark", code: "DK", dialCode: "+45", flag: "🇩🇰", minLength: 8, maxLength: 8 },
  { name: "Finland", code: "FI", dialCode: "+358", flag: "🇫🇮", minLength: 9, maxLength: 10 },
  { name: "Ireland", code: "IE", dialCode: "+353", flag: "🇮🇪", minLength: 9, maxLength: 9 },
  { name: "Portugal", code: "PT", dialCode: "+351", flag: "🇵🇹", minLength: 9, maxLength: 9 },
  { name: "Greece", code: "GR", dialCode: "+30", flag: "🇬🇷", minLength: 10, maxLength: 10 },
  { name: "Czech Republic", code: "CZ", dialCode: "+420", flag: "🇨🇿", minLength: 9, maxLength: 9 },
  { name: "Romania", code: "RO", dialCode: "+40", flag: "🇷🇴", minLength: 10, maxLength: 10 },
  { name: "Hungary", code: "HU", dialCode: "+36", flag: "🇭🇺", minLength: 9, maxLength: 9 },
  { name: "Russia", code: "RU", dialCode: "+7", flag: "🇷🇺", minLength: 10, maxLength: 10 },
  { name: "Ukraine", code: "UA", dialCode: "+380", flag: "🇺🇦", minLength: 9, maxLength: 9 },
  { name: "Turkey", code: "TR", dialCode: "+90", flag: "🇹🇷", minLength: 10, maxLength: 10 },
  { name: "Saudi Arabia", code: "SA", dialCode: "+966", flag: "🇸🇦", minLength: 9, maxLength: 9 },
  {
    name: "United Arab Emirates",
    code: "AE",
    dialCode: "+971",
    flag: "🇦🇪",
    minLength: 9,
    maxLength: 9,
  },
  { name: "Israel", code: "IL", dialCode: "+972", flag: "🇮🇱", minLength: 9, maxLength: 9 },
  { name: "South Africa", code: "ZA", dialCode: "+27", flag: "🇿🇦", minLength: 9, maxLength: 9 },
  { name: "Egypt", code: "EG", dialCode: "+20", flag: "🇪🇬", minLength: 10, maxLength: 10 },
  { name: "Nigeria", code: "NG", dialCode: "+234", flag: "🇳🇬", minLength: 10, maxLength: 10 },
  { name: "Kenya", code: "KE", dialCode: "+254", flag: "🇰🇪", minLength: 9, maxLength: 10 },
  { name: "Singapore", code: "SG", dialCode: "+65", flag: "🇸🇬", minLength: 8, maxLength: 8 },
  { name: "Malaysia", code: "MY", dialCode: "+60", flag: "🇲🇾", minLength: 9, maxLength: 10 },
  { name: "Thailand", code: "TH", dialCode: "+66", flag: "🇹🇭", minLength: 9, maxLength: 9 },
  { name: "Indonesia", code: "ID", dialCode: "+62", flag: "🇮🇩", minLength: 10, maxLength: 12 },
  { name: "Philippines", code: "PH", dialCode: "+63", flag: "🇵🇭", minLength: 10, maxLength: 10 },
  { name: "Vietnam", code: "VN", dialCode: "+84", flag: "🇻🇳", minLength: 9, maxLength: 10 },
  { name: "Pakistan", code: "PK", dialCode: "+92", flag: "🇵🇰", minLength: 10, maxLength: 10 },
  { name: "Bangladesh", code: "BD", dialCode: "+880", flag: "🇧🇩", minLength: 10, maxLength: 10 },
  { name: "Argentina", code: "AR", dialCode: "+54", flag: "🇦🇷", minLength: 10, maxLength: 10 },
  { name: "Chile", code: "CL", dialCode: "+56", flag: "🇨🇱", minLength: 9, maxLength: 9 },
  { name: "Colombia", code: "CO", dialCode: "+57", flag: "🇨🇴", minLength: 10, maxLength: 10 },
  { name: "Peru", code: "PE", dialCode: "+51", flag: "🇵🇪", minLength: 9, maxLength: 9 },
  { name: "Venezuela", code: "VE", dialCode: "+58", flag: "🇻🇪", minLength: 10, maxLength: 10 },
  { name: "New Zealand", code: "NZ", dialCode: "+64", flag: "🇳🇿", minLength: 9, maxLength: 10 },
  { name: "Luxembourg", code: "LU", dialCode: "+352", flag: "🇱🇺", minLength: 9, maxLength: 9 },
  { name: "Iceland", code: "IS", dialCode: "+354", flag: "🇮🇸", minLength: 7, maxLength: 7 },
  { name: "Croatia", code: "HR", dialCode: "+385", flag: "🇭🇷", minLength: 9, maxLength: 9 },
  { name: "Slovenia", code: "SI", dialCode: "+386", flag: "🇸🇮", minLength: 8, maxLength: 8 },
  { name: "Slovakia", code: "SK", dialCode: "+421", flag: "🇸🇰", minLength: 9, maxLength: 9 },
  { name: "Bulgaria", code: "BG", dialCode: "+359", flag: "🇧🇬", minLength: 9, maxLength: 9 },
  { name: "Lithuania", code: "LT", dialCode: "+370", flag: "🇱🇹", minLength: 8, maxLength: 8 },
  { name: "Latvia", code: "LV", dialCode: "+371", flag: "🇱🇻", minLength: 8, maxLength: 8 },
  { name: "Estonia", code: "EE", dialCode: "+372", flag: "🇪🇪", minLength: 7, maxLength: 8 },
  { name: "Malta", code: "MT", dialCode: "+356", flag: "🇲🇹", minLength: 8, maxLength: 8 },
  { name: "Cyprus", code: "CY", dialCode: "+357", flag: "🇨🇾", minLength: 8, maxLength: 8 },
  { name: "Hong Kong", code: "HK", dialCode: "+852", flag: "🇭🇰", minLength: 8, maxLength: 8 },
  { name: "Taiwan", code: "TW", dialCode: "+886", flag: "🇹🇼", minLength: 9, maxLength: 9 },
  { name: "Qatar", code: "QA", dialCode: "+974", flag: "🇶🇦", minLength: 8, maxLength: 8 },
  { name: "Kuwait", code: "KW", dialCode: "+965", flag: "🇰🇼", minLength: 8, maxLength: 8 },
  { name: "Bahrain", code: "BH", dialCode: "+973", flag: "🇧🇭", minLength: 8, maxLength: 8 },
  { name: "Oman", code: "OM", dialCode: "+968", flag: "🇴🇲", minLength: 8, maxLength: 8 },
  { name: "Jordan", code: "JO", dialCode: "+962", flag: "🇯🇴", minLength: 9, maxLength: 9 },
  { name: "Lebanon", code: "LB", dialCode: "+961", flag: "🇱🇧", minLength: 7, maxLength: 8 },
  { name: "Morocco", code: "MA", dialCode: "+212", flag: "🇲🇦", minLength: 9, maxLength: 9 },
  { name: "Algeria", code: "DZ", dialCode: "+213", flag: "🇩🇿", minLength: 9, maxLength: 9 },
  { name: "Tunisia", code: "TN", dialCode: "+216", flag: "🇹🇳", minLength: 8, maxLength: 8 },
  { name: "Ghana", code: "GH", dialCode: "+233", flag: "🇬🇭", minLength: 9, maxLength: 9 },
  { name: "Ethiopia", code: "ET", dialCode: "+251", flag: "🇪🇹", minLength: 9, maxLength: 9 },
  { name: "Uganda", code: "UG", dialCode: "+256", flag: "🇺🇬", minLength: 9, maxLength: 9 },
  { name: "Tanzania", code: "TZ", dialCode: "+255", flag: "🇹🇿", minLength: 9, maxLength: 9 },
  { name: "Zimbabwe", code: "ZW", dialCode: "+263", flag: "🇿🇼", minLength: 9, maxLength: 9 },
  { name: "Zambia", code: "ZM", dialCode: "+260", flag: "🇿🇲", minLength: 9, maxLength: 9 },
  { name: "Botswana", code: "BW", dialCode: "+267", flag: "🇧🇼", minLength: 7, maxLength: 8 },
  { name: "Namibia", code: "NA", dialCode: "+264", flag: "🇳🇦", minLength: 9, maxLength: 9 },
  { name: "Mozambique", code: "MZ", dialCode: "+258", flag: "🇲🇿", minLength: 9, maxLength: 9 },
  { name: "Angola", code: "AO", dialCode: "+244", flag: "🇦🇴", minLength: 9, maxLength: 9 },
  { name: "Senegal", code: "SN", dialCode: "+221", flag: "🇸🇳", minLength: 9, maxLength: 9 },
  { name: "Ivory Coast", code: "CI", dialCode: "+225", flag: "🇨🇮", minLength: 8, maxLength: 10 },
  { name: "Cameroon", code: "CM", dialCode: "+237", flag: "🇨🇲", minLength: 9, maxLength: 9 },
  { name: "Sri Lanka", code: "LK", dialCode: "+94", flag: "🇱🇰", minLength: 9, maxLength: 9 },
  { name: "Myanmar", code: "MM", dialCode: "+95", flag: "🇲🇲", minLength: 9, maxLength: 10 },
  { name: "Cambodia", code: "KH", dialCode: "+855", flag: "🇰🇭", minLength: 8, maxLength: 9 },
  { name: "Nepal", code: "NP", dialCode: "+977", flag: "🇳🇵", minLength: 10, maxLength: 10 },
  { name: "Afghanistan", code: "AF", dialCode: "+93", flag: "🇦🇫", minLength: 9, maxLength: 9 },
  { name: "Iraq", code: "IQ", dialCode: "+964", flag: "🇮🇶", minLength: 10, maxLength: 10 },
  { name: "Iran", code: "IR", dialCode: "+98", flag: "🇮🇷", minLength: 10, maxLength: 10 },
  { name: "Albania", code: "AL", dialCode: "+355", flag: "🇦🇱", minLength: 9, maxLength: 9 },
  { name: "Armenia", code: "AM", dialCode: "+374", flag: "🇦🇲", minLength: 8, maxLength: 8 },
  { name: "Azerbaijan", code: "AZ", dialCode: "+994", flag: "🇦🇿", minLength: 9, maxLength: 9 },
  { name: "Belarus", code: "BY", dialCode: "+375", flag: "🇧🇾", minLength: 9, maxLength: 9 },
  {
    name: "Bosnia and Herzegovina",
    code: "BA",
    dialCode: "+387",
    flag: "🇧🇦",
    minLength: 8,
    maxLength: 8,
  },
  { name: "Georgia", code: "GE", dialCode: "+995", flag: "🇬🇪", minLength: 9, maxLength: 9 },
  { name: "Kazakhstan", code: "KZ", dialCode: "+7", flag: "🇰🇿", minLength: 10, maxLength: 10 },
  { name: "Kyrgyzstan", code: "KG", dialCode: "+996", flag: "🇰🇬", minLength: 9, maxLength: 9 },
  { name: "Macedonia", code: "MK", dialCode: "+389", flag: "🇲🇰", minLength: 8, maxLength: 8 },
  { name: "Moldova", code: "MD", dialCode: "+373", flag: "🇲🇩", minLength: 8, maxLength: 8 },
  { name: "Mongolia", code: "MN", dialCode: "+976", flag: "🇲🇳", minLength: 8, maxLength: 8 },
  { name: "Montenegro", code: "ME", dialCode: "+382", flag: "🇲🇪", minLength: 8, maxLength: 8 },
  { name: "Serbia", code: "RS", dialCode: "+381", flag: "🇷🇸", minLength: 8, maxLength: 9 },
  { name: "Tajikistan", code: "TJ", dialCode: "+992", flag: "🇹🇯", minLength: 9, maxLength: 9 },
  { name: "Turkmenistan", code: "TM", dialCode: "+993", flag: "🇹🇲", minLength: 8, maxLength: 8 },
  { name: "Uzbekistan", code: "UZ", dialCode: "+998", flag: "🇺🇿", minLength: 9, maxLength: 9 },
  { name: "Laos", code: "LA", dialCode: "+856", flag: "🇱🇦", minLength: 9, maxLength: 10 },
  { name: "Brunei", code: "BN", dialCode: "+673", flag: "🇧🇳", minLength: 7, maxLength: 7 },
  { name: "Maldives", code: "MV", dialCode: "+960", flag: "🇲🇻", minLength: 7, maxLength: 7 },
  { name: "Bhutan", code: "BT", dialCode: "+975", flag: "🇧🇹", minLength: 8, maxLength: 8 },
  { name: "Bolivia", code: "BO", dialCode: "+591", flag: "🇧🇴", minLength: 8, maxLength: 8 },
  { name: "Costa Rica", code: "CR", dialCode: "+506", flag: "🇨🇷", minLength: 8, maxLength: 8 },
  { name: "Cuba", code: "CU", dialCode: "+53", flag: "🇨🇺", minLength: 8, maxLength: 8 },
  {
    name: "Dominican Republic",
    code: "DO",
    dialCode: "+1-809",
    flag: "🇩🇴",
    minLength: 10,
    maxLength: 10,
  },
  { name: "Ecuador", code: "EC", dialCode: "+593", flag: "🇪🇨", minLength: 9, maxLength: 9 },
  { name: "El Salvador", code: "SV", dialCode: "+503", flag: "🇸🇻", minLength: 8, maxLength: 8 },
  { name: "Guatemala", code: "GT", dialCode: "+502", flag: "🇬🇹", minLength: 8, maxLength: 8 },
  { name: "Haiti", code: "HT", dialCode: "+509", flag: "🇭🇹", minLength: 8, maxLength: 8 },
  { name: "Honduras", code: "HN", dialCode: "+504", flag: "🇭🇳", minLength: 8, maxLength: 8 },
  { name: "Jamaica", code: "JM", dialCode: "+1-876", flag: "🇯🇲", minLength: 10, maxLength: 10 },
  { name: "Nicaragua", code: "NI", dialCode: "+505", flag: "🇳🇮", minLength: 8, maxLength: 8 },
  { name: "Panama", code: "PA", dialCode: "+507", flag: "🇵🇦", minLength: 8, maxLength: 8 },
  { name: "Paraguay", code: "PY", dialCode: "+595", flag: "🇵🇾", minLength: 9, maxLength: 9 },
  { name: "Puerto Rico", code: "PR", dialCode: "+1-787", flag: "🇵🇷", minLength: 10, maxLength: 10 },
  { name: "Suriname", code: "SR", dialCode: "+597", flag: "🇸🇷", minLength: 7, maxLength: 7 },
  {
    name: "Trinidad and Tobago",
    code: "TT",
    dialCode: "+1-868",
    flag: "🇹🇹",
    minLength: 10,
    maxLength: 10,
  },
  { name: "Uruguay", code: "UY", dialCode: "+598", flag: "🇺🇾", minLength: 8, maxLength: 8 },
  { name: "Guyana", code: "GY", dialCode: "+592", flag: "🇬🇾", minLength: 7, maxLength: 7 },
  { name: "Fiji", code: "FJ", dialCode: "+679", flag: "🇫🇯", minLength: 7, maxLength: 7 },
  {
    name: "Papua New Guinea",
    code: "PG",
    dialCode: "+675",
    flag: "🇵🇬",
    minLength: 8,
    maxLength: 8,
  },
  { name: "Samoa", code: "WS", dialCode: "+685", flag: "🇼🇸", minLength: 7, maxLength: 7 },
  { name: "Tonga", code: "TO", dialCode: "+676", flag: "🇹🇴", minLength: 5, maxLength: 7 },
  { name: "Vanuatu", code: "VU", dialCode: "+678", flag: "🇻🇺", minLength: 7, maxLength: 7 },
  { name: "Mauritius", code: "MU", dialCode: "+230", flag: "🇲🇺", minLength: 8, maxLength: 8 },
  { name: "Seychelles", code: "SC", dialCode: "+248", flag: "🇸🇨", minLength: 7, maxLength: 7 },
  { name: "Madagascar", code: "MG", dialCode: "+261", flag: "🇲🇬", minLength: 9, maxLength: 9 },
  { name: "Reunion", code: "RE", dialCode: "+262", flag: "🇷🇪", minLength: 9, maxLength: 9 },
  { name: "Mayotte", code: "YT", dialCode: "+262", flag: "🇾🇹", minLength: 9, maxLength: 9 },
  { name: "Barbados", code: "BB", dialCode: "+1-246", flag: "🇧🇧", minLength: 10, maxLength: 10 },
  { name: "Bermuda", code: "BM", dialCode: "+1-441", flag: "🇧🇲", minLength: 10, maxLength: 10 },
  {
    name: "Cayman Islands",
    code: "KY",
    dialCode: "+1-345",
    flag: "🇰🇾",
    minLength: 10,
    maxLength: 10,
  },
  { name: "Greenland", code: "GL", dialCode: "+299", flag: "🇬🇱", minLength: 6, maxLength: 6 },
  { name: "Faroe Islands", code: "FO", dialCode: "+298", flag: "🇫🇴", minLength: 6, maxLength: 6 },
  { name: "Liechtenstein", code: "LI", dialCode: "+423", flag: "🇱🇮", minLength: 7, maxLength: 9 },
  { name: "Monaco", code: "MC", dialCode: "+377", flag: "🇲🇨", minLength: 8, maxLength: 9 },
  { name: "San Marino", code: "SM", dialCode: "+378", flag: "🇸🇲", minLength: 10, maxLength: 10 },
  { name: "Vatican City", code: "VA", dialCode: "+379", flag: "🇻🇦", minLength: 10, maxLength: 10 },
  { name: "Andorra", code: "AD", dialCode: "+376", flag: "🇦🇩", minLength: 6, maxLength: 9 },
];

/**
 * Get country by dial code
 * @param {string} dialCode - Country dial code (e.g., "+1", "+91")
 * @returns {object|null} Country object or null if not found
 */
export const getCountryByDialCode = (dialCode) => {
  return COUNTRY_CODES.find((country) => country.dialCode === dialCode) || null;
};

/**
 * Get country by country code (ISO 3166-1 alpha-2)
 * @param {string} code - Two-letter country code (e.g., "US", "IN")
 * @returns {object|null} Country object or null if not found
 */
export const getCountryByCode = (code) => {
  return COUNTRY_CODES.find((country) => country.code === code) || null;
};

/**
 * Validate mobile number length for a specific country
 * @param {string} phoneNumber - Phone number without country code
 * @param {string} dialCode - Country dial code
 * @returns {object} Validation result { isValid: boolean, message: string }
 */
export const validateMobileNumber = (phoneNumber, dialCode) => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return {
      isValid: false,
      message: "Mobile number is required",
    };
  }

  if (!dialCode || dialCode.trim() === "") {
    return {
      isValid: false,
      message: "Country code is required",
    };
  }

  const country = getCountryByDialCode(dialCode);

  if (!country) {
    return {
      isValid: false,
      message: "Invalid country code",
    };
  }

  // Remove all non-digit characters
  const cleanNumber = phoneNumber.replace(/\D/g, "");

  if (cleanNumber.length < country.minLength) {
    return {
      isValid: false,
      message: `Mobile number must be at least ${country.minLength} digits for ${country.name}`,
    };
  }

  if (cleanNumber.length > country.maxLength) {
    return {
      isValid: false,
      message: `Mobile number cannot exceed ${country.maxLength} digits for ${country.name}`,
    };
  }

  // Check if number contains only digits
  if (!/^\d+$/.test(cleanNumber)) {
    return {
      isValid: false,
      message: "Mobile number must contain only digits",
    };
  }

  return {
    isValid: true,
    message: "Valid mobile number",
  };
};

/**
 * Format phone number for display
 * @param {string} phoneNumber - Phone number without country code
 * @param {string} dialCode - Country dial code
 * @returns {string} Formatted phone number with country code
 */
export const formatPhoneNumber = (phoneNumber, dialCode) => {
  if (!phoneNumber || !dialCode) return "";

  const cleanNumber = phoneNumber.replace(/\D/g, "");
  return `${dialCode} ${cleanNumber}`;
};

/**
 * Parse full phone number into country code and number
 * @param {string} fullNumber - Full phone number with country code
 * @returns {object} Parsed phone number { dialCode: string, number: string }
 */
export const parsePhoneNumber = (fullNumber) => {
  if (!fullNumber) return { dialCode: "", number: "" };

  // Find matching country code
  const matchedCountry = COUNTRY_CODES.find((country) => fullNumber.startsWith(country.dialCode));

  if (matchedCountry) {
    const dialCode = matchedCountry.dialCode;
    const number = fullNumber.substring(dialCode.length).trim();
    return { dialCode, number };
  }

  return { dialCode: "", number: fullNumber };
};

/**
 * Get formatted country options for dropdown/select
 * Sorted alphabetically by country name
 * @returns {Array} Array of country options with label and value
 */
export const getCountryOptions = () => {
  return COUNTRY_CODES.sort((a, b) => a.name.localeCompare(b.name)).map((country) => ({
    label: `${country.flag} ${country.name} (${country.dialCode})`,
    value: country.dialCode,
    country: country,
  }));
};

/**
 * Get country information including min/max length
 * @param {string} dialCode - Country dial code
 * @returns {object|null} Country info or null
 */
export const getCountryInfo = (dialCode) => {
  const country = getCountryByDialCode(dialCode);
  if (!country) return null;

  return {
    name: country.name,
    code: country.code,
    dialCode: country.dialCode,
    flag: country.flag,
    minLength: country.minLength,
    maxLength: country.maxLength,
    lengthInfo: `${country.minLength}${country.minLength !== country.maxLength ? "-" + country.maxLength : ""} digits`,
  };
};

/**
 * Check if country code selection is required and show error
 * @param {string} dialCode - Selected country dial code
 * @returns {object} Error state { hasError: boolean, errorMessage: string }
 */
export const checkCountryCodeRequired = (dialCode) => {
  if (!dialCode || dialCode.trim() === "") {
    return {
      hasError: true,
      errorMessage: "Please select a country code",
    };
  }

  return {
    hasError: false,
    errorMessage: "",
  };
};

/**
 * Generate OTP delivery message based on selected method
 * @param {string} method - Delivery method ('email', 'sms', 'both')
 * @param {string} email - User email
 * @param {string} phoneNumber - User phone number with country code
 * @returns {string} OTP delivery message
 */
export const getOTPDeliveryMessage = (method, email, phoneNumber) => {
  switch (method.toLowerCase()) {
    case "email":
      return `OTP has been sent to ${email}`;
    case "sms":
      return `OTP has been sent to ${phoneNumber}`;
    case "both":
      return `OTP has been sent to ${email} and ${phoneNumber}`;
    default:
      return "OTP has been sent";
  }
};

export default {
  COUNTRY_CODES,
  getCountryByDialCode,
  getCountryByCode,
  validateMobileNumber,
  formatPhoneNumber,
  parsePhoneNumber,
  getCountryOptions,
  getCountryInfo,
  checkCountryCodeRequired,
  getOTPDeliveryMessage,
};
